import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Search, PlusCircle, Loader2, Phone, Hand } from "lucide-react";
import { toast } from "sonner";
import { api, formatRp, statusMeta, getStatusDisplay, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const baseTabs = [
  { key: "all", label: "Semua" },
  { key: "masuk", label: "Masuk" },
  { key: "proses", label: "Proses" },
  { key: "selesai", label: "Selesai" },
  { key: "diambil", label: "Diambil" },
];

export default function ServicesPage() {
  const { user } = useAuth();
  const isTech = user?.role === "technician";
  const tabs = isTech
    ? [{ key: "unassigned", label: "Belum Diambil" }, { key: "mine", label: "Milik Saya" }, ...baseTabs]
    : baseTabs;
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState(isTech ? "unassigned" : "all");
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState(null);

  const load = () => {
    setLoading(true);
    const params = {};
    if (status === "mine") params.mine = true;
    else if (status === "unassigned") params.unassigned = true;
    else if (status !== "all") params.status = status;
    if (q.trim()) params.q = q.trim();
    api
      .get("/services", { params })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, [status]);

  const claim = async (svc, e) => {
    e?.preventDefault();
    e?.stopPropagation();
    setClaiming(svc.id);
    try {
      await api.post(`/services/${svc.id}/claim`);
      toast.success(`${svc.ticket_no} berhasil diambil`);
      load();
    } catch (err) {
      const d = err?.response?.data?.detail || "Gagal mengambil service";
      toast.error(typeof d === "string" ? d : "Gagal mengambil service");
    } finally {
      setClaiming(null);
    }
  };

  return (
    <div className="space-y-5" data-testid="services-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
            Daftar Barang
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Kelola semua barang service dan lacak statusnya.
          </p>
        </div>
        {can.createService(user?.role) && (
          <Link to="/intake">
            <Button data-testid="add-service-button" className="bg-slate-900 hover:bg-slate-800 gap-2">
              <PlusCircle className="w-4 h-4" /> Tambah Barang
            </Button>
          </Link>
        )}
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input
              data-testid="services-search"
              placeholder="Cari nama pelanggan / no HP / no. tiket / merek..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && load()}
              className="pl-9"
            />
          </div>
          <Button variant="outline" onClick={load} data-testid="services-search-btn">
            Cari
          </Button>
        </div>

        <div className="mt-4 flex gap-2 overflow-x-auto scrollbar-thin pb-1">
          {tabs.map((t) => (
            <button
              key={t.key}
              data-testid={`status-filter-${t.key}`}
              onClick={() => setStatus(t.key)}
              className={`px-3 py-1.5 text-xs font-semibold uppercase tracking-wider rounded-full border transition-all whitespace-nowrap ${
                status === t.key
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-400"
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      ) : items.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-10 text-center">
          <p className="text-slate-500">Tidak ada barang untuk filter ini.</p>
          <Link to="/intake" className="inline-block mt-3">
            <Button variant="outline" className="gap-2">
              <PlusCircle className="w-4 h-4" /> Tambah Barang
            </Button>
          </Link>
        </div>
      ) : (
        <div className="grid gap-3 md:hidden">
          {items.map((s) => (
            <Link
              key={s.id}
              to={`/services/${s.id}`}
              data-testid={`service-card-${s.ticket_no}`}
              className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm hover:shadow-md transition-all"
            >
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-mono text-xs text-slate-500">{s.ticket_no}</span>
                <span
                  className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${getStatusDisplay(s).cls}`}
                >
                  {getStatusDisplay(s).label}
                </span>
              </div>
              <div className="mt-2 font-medium text-slate-900">{s.customer_name}</div>
              {!isTech && (
                <div className="text-xs text-slate-500 flex items-center gap-1 mt-0.5">
                  <Phone className="w-3 h-3" /> {s.customer_phone}
                </div>
              )}
              <div className="text-xs text-slate-600 mt-1">
                {s.item_brand} · {s.item_type}
              </div>
              <div className="flex items-center justify-between mt-3 pt-2 border-t border-slate-100">
                <span className="text-[11px] text-slate-500">
                  {s.assigned_tech_name ? (
                    <span className="text-emerald-700">👨‍🔧 {s.assigned_tech_name}</span>
                  ) : (
                    <span className="text-amber-600">Belum diambil</span>
                  )}
                </span>
                {isTech && !s.assigned_tech_id ? (
                  <Button
                    size="sm"
                    className="h-7 gap-1 bg-emerald-600 hover:bg-emerald-700 text-white text-xs"
                    onClick={(e) => claim(s, e)}
                    disabled={claiming === s.id}
                    data-testid={`claim-btn-${s.ticket_no}`}
                  >
                    <Hand className="w-3 h-3" /> Ambil
                  </Button>
                ) : (
                  <span className="text-sm font-semibold text-slate-800">
                    {formatRp(s.final_cost || s.estimated_cost)}
                  </span>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}

      {!loading && items.length > 0 && (
        <div className="hidden md:block bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr className="text-left text-xs uppercase tracking-wider text-slate-500">
                <th className="px-4 py-3 font-semibold">No. Tiket</th>
                <th className="px-4 py-3 font-semibold">Pelanggan</th>
                <th className="px-4 py-3 font-semibold">Barang</th>
                <th className="px-4 py-3 font-semibold">Tanggal</th>
                <th className="px-4 py-3 font-semibold">Status</th>
                <th className="px-4 py-3 font-semibold text-right">Biaya</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((s) => (
                <tr
                  key={s.id}
                  data-testid={`service-row-${s.ticket_no}`}
                  className="hover:bg-slate-50 cursor-pointer"
                  onClick={() => (window.location.href = `/services/${s.id}`)}
                >
                  <td className="px-4 py-3 font-mono text-xs text-slate-600">{s.ticket_no}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">{s.customer_name}</div>
                    {!isTech && (
                      <div className="text-xs text-slate-500">{s.customer_phone}</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-slate-800">{s.item_brand}</div>
                    <div className="text-xs text-slate-500">{s.item_type}</div>
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">{formatDate(s.created_at)}</td>
                  <td className="px-4 py-3">
                    <span
                      className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full border ${getStatusDisplay(s).cls}`}
                    >
                      {getStatusDisplay(s).label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-slate-800">
                    {formatRp(s.final_cost || s.estimated_cost)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
